import React, { useState, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Slider,
    Avatar,
    Tooltip,
    Chip,
    Divider,
    useMediaQuery,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ArrowForward as ArrowForwardIcon,
    CheckCircleOutline as CheckCircleIcon,
    TrendingUp as TrendingUpIcon,
    Security as SecurityIcon,
    Savings as SavingsIcon,
    CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import SEO from '../components/SEO/SEO';
import { getSeoData } from '../utils/seoConfig';
// Mocking these imports for standalone functionality
import { getAppTheme } from '../services/authContext';
const useSelector = (selector) => ({ mode: 'light' });


// --- CONFIGURATION & DATA ---
const FD_CTA_LINK = "https://makeloaneasy-uat0.tarrakki.com/";

const heroData = {
    title: "Invest in India's Favourite Investment Product",
    subtitle: "Best Rate up to 9.5% p.a.",
    features: ["Bank FDs insured up to ₹5L by RBI's subsidiary", "Quick Withdrawal of FDs"]
};

const fdProductsData = [
    // {
    //     id: '4',
    //     name: 'Unity Small Finance Bank Limited',
    //     logoUrl: 'https://cdn.tarrakki.com/images/unity_logo.svg',
    //     tags: [],
    //     interestRate: '4.5% - 9.5%',
    //     tenure: '7 Days - 1 Year',
    //     minDeposit: '₹ 1,000',
    //     maxDeposit: '₹ 2,00,00,000',
    //     lockIn: '7 Days',
    //     ctaLink: 'https://makeloaneasy-uat0.tarrakki.com/'
    // },
    {
        id: '1',
        name: 'Shriram Finance Limited',
        logoUrl: 'https://cdn.tarrakki.com/images/sfl-logo.webp',
        tags: ['Recommended', 'FAAA', 'AA'],
        interestRate: '7.59% - 9.45%',
        tenure: '12 Months - 60 Months',
        minDeposit: '₹ 5,000',
        maxDeposit: '₹ 10,00,00,000',
        lockIn: '3 Months',
        ctaLink: 'https://makeloaneasy-uat0.tarrakki.com/'
    },
    {
        id: '2',
        name: 'Mahindra and Mahindra Finance Limited',
        logoUrl: 'https://cdn.tarrakki.com/images/mfl-logo.png',
        tags: ['Recommended', 'FAAA', 'AAA'],
        interestRate: '7.1% - 8.35%',
        tenure: '12 Months - 60 Months',
        minDeposit: '₹ 5,000',
        maxDeposit: '₹ 5,00,00,000',
        lockIn: '3 Months',
        ctaLink: 'https://makeloaneasy-uat0.tarrakki.com/'
    },
    // {
    //     id: '3',
    //     name: 'Bajaj Finance Limited',
    //     logoUrl: 'https://cdn.tarrakki.com/images/BFL%20Primary%20Logo.png',
    //     tags: ['AAA', '[ICRA]AAA(STABLE)', 'CRISIL AAA/STABLE'],
    //     interestRate: '7.16% - 8.85%',
    //     tenure: '12 Months - 60 Months',
    //     minDeposit: '₹ 15,000',
    //     maxDeposit: '₹ 3,00,00,000',
    //     lockIn: '3 Months',
    //     ctaLink: 'https://makeloaneasy-uat0.tarrakki.com/'
    // }
];

// --- UPDATED BENEFITS DATA ---
const benefitsData = [
    { title: "Assured Returns", description: "No matter how interest rates move, you get the returns fixed at the time of investing.", icon: <SecurityIcon /> },
    { title: "Faster Growth", description: "You earn interest on the interest amount too. This helps you accumulate a big amount with ease.", icon: <TrendingUpIcon /> },
    { title: "Acts as Emergency Fund", description: "You can liquidate your FDs in case of emergencies, often with a minimal penalty, providing a reliable safety net.", icon: <SavingsIcon /> },
    { title: "Loan Against FD", description: "Get a loan of up to 90% of your FD amount at attractive interest rates without breaking the deposit.", icon: <CreditCardIcon /> },
];

// --- NEW ALTERNATING BENEFITS DATA ---
const alternatingBenefitsData = [
    {
        title: "Flexible Tenure Options",
        description: "Choose an investment duration that aligns perfectly with your financial goals, from as short as 7 days to as long as 10 years. This flexibility allows you to plan for both short-term needs and long-term aspirations.",
        imageUrl: "/img_frontend/mle/mleh3.jpg"
    },
    {
        title: "Tax-Saver Fixed Deposits",
        description: "Invest in a Tax-Saver FD for a tenure of 5 years and claim a deduction of up to ₹1.5 lakh under Section 80C of the Income Tax Act. It's a great way to save on taxes while securing your investment.",
        imageUrl: "/img_frontend/mle/mleh4.jpg"
    }
];


const faqData = [
    { q: 'Why Invest in FD?', a: 'Fixed Deposits are a safe and stable investment option that offers guaranteed returns, regardless of market fluctuations. They are ideal for conservative investors seeking capital protection and steady interest income.' },
    { q: 'How can I calculate the interest on my Fixed Deposit?', a: 'You can use our online FD calculator. Simply enter the investment amount, tenure, and interest rate to see the maturity amount and interest earned.' },
    { q: 'Is it safe to invest in an FD on this platform?', a: 'Yes, it is completely safe. We partner with RBI-regulated banks and trusted NBFCs. Bank FDs are insured up to ₹5 lakh by DICGC, an RBI subsidiary.' },
];

// --- REUSABLE COMPONENTS ---

const Section = ({ children, sx = {}, ...props }) => (
    <Box component="section" sx={{ py: { xs: 6, md: 8 }, ...sx }} {...props}>
        <Container maxWidth="lg">
            {children}
        </Container>
    </Box>
);

const SectionTitle = ({ title, subtitle }) => (
    <Box sx={{ textAlign: 'center', mb: 5 }}>
        <Typography variant="h3" component="h2" gutterBottom>{title}</Typography>
        {subtitle && <Typography variant="h6" color="text.secondary">{subtitle}</Typography>}
    </Box>
);

const ValueLabelComponent = (props) => {
    const { children, value } = props;
    return <Tooltip enterTouchDelay={0} placement="top" title={value.toLocaleString('en-IN')}>{children}</Tooltip>;
};

const CalculatorInput = ({ label, value, onChange, unit, min, max, step }) => (
    <Box mb={2}>
        <Typography gutterBottom>{label}</Typography>
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
                <Slider value={value} onChange={(e, val) => onChange(val)} min={min} max={max} step={step} components={{ ValueLabel: ValueLabelComponent }} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField
                    value={value.toLocaleString('en-IN')}
                    onChange={(e) => {
                        const numericValue = Number(e.target.value.replace(/,/g, ''));
                        if (!isNaN(numericValue)) onChange(numericValue > max ? max : numericValue);
                    }}
                    size="small"
                    InputProps={{ startAdornment: <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>{unit}</Typography> }}
                    variant="outlined"
                />
            </Grid>
        </Grid>
    </Box>
);

// --- PAGE SECTIONS ---
const HeroSection = ({ data }) => (
    <Section sx={{ backgroundColor: 'primary.main', color: 'white', py: 8 }}>
        <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
                <Typography variant="h2" component="h1" gutterBottom>{data.title}</Typography>
                <Typography variant="h5" sx={{ mb: 2 }}>{data.subtitle}</Typography>
                {data.features.map((feature, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircleIcon sx={{ mr: 1 }} />
                        <Typography>{feature}</Typography>
                    </Box>
                ))}
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, textAlign: 'center',overflowX: 'hidden'}}>
                    <img
                        src="/img_frontend/mle/undraw_savings_uwjn.svg"
                        alt="Savings Illustration"
                        style={{ maxWidth: '400px', height: 'auto', marginInline: "auto", marginBottom: '16px' }}
                    />
                    <Typography variant="h5" color="text.primary" gutterBottom>Ready to Invest?</Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>Compare top FD rates and book your FD in minutes.</Typography>
                    <Button variant="contained" size="large" href={FD_CTA_LINK} target="_blank" endIcon={<ArrowForwardIcon />}>
                        Invest in FDs Now
                    </Button>
                </Paper>
            </Grid>
        </Grid>
    </Section>
);

// --- FD PRODUCTS SECTION (WITH LOGO FIX) ---
const FDProductCard = ({ product }) => {
    const theme = getAppTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const logoContainerStyles = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'transparent',
        overflow: 'hidden'
    };

    const logoStyles = {
        width: '100%',
        height: '100%',
        objectFit: 'contain'
    };

    if (!isDesktop) {
        // Mobile Card Layout
        return (
            <Card sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs="auto">
                            <Avatar variant="rounded" sx={{ ...logoContainerStyles, width: 58, height: 58 }}>
                                <img src={product.logoUrl} alt={`${product.name} logo`} style={logoStyles} />
                            </Avatar>
                        </Grid>
                        <Grid item xs>
                            <Typography variant="subtitle1" fontWeight="bold">{product.name}</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 0.5 }}>
                                {product.tags.slice(0, 2).map(tag => <Chip key={tag} label={tag} size="small" color="primary" variant="outlined" />)}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Interest Rate: <Box component="span" fontWeight="bold">{product.interestRate}</Box>
                            </Typography>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 1.5 }} />
                    <Grid container spacing={1} textAlign="center">
                        <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary">Tenure</Typography>
                            <Typography variant="body2" fontWeight="medium">{product.tenure}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary">Min. Deposit</Typography>
                            <Typography variant="body2" fontWeight="medium">{product.minDeposit}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary">Max. Deposit</Typography>
                            <Typography variant="body2" fontWeight="medium">{product.maxDeposit}</Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    }

    // Desktop Card Layout
    return (
        <Card sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
            <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs="auto">
                        <Avatar variant="rounded" sx={{ ...logoContainerStyles, width: 84, height: 84 }}>
                            <img src={product.logoUrl} alt={`${product.name} logo`} style={logoStyles} />
                        </Avatar>
                    </Grid>
                    <Grid item xs>
                        <Typography variant="h5" fontWeight="bold">{product.name}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {product.tags.map(tag => <Chip key={tag} label={tag} size="small" color="success" variant="outlined" />)}
                        </Box>
                    </Grid>
                    <Grid item xs="auto">
                        <Button variant="contained" href={product.ctaLink} target="_blank">View Details</Button>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} textAlign="left">
                    <Grid item xs>
                        <Typography variant="body2" color="text.secondary">Interest Rate (% p.a.)</Typography>
                        <Typography variant="h6" fontWeight="medium">{product.interestRate}</Typography>
                    </Grid>
                    <Grid item xs>
                        <Typography variant="body2" color="text.secondary">Tenure</Typography>
                        <Typography variant="h6" fontWeight="medium">{product.tenure}</Typography>
                    </Grid>
                    <Grid item xs>
                        <Typography variant="body2" color="text.secondary">Min. Deposit</Typography>
                        <Typography variant="h6" fontWeight="medium">{product.minDeposit}</Typography>
                    </Grid>
                    <Grid item xs>
                        <Typography variant="body2" color="text.secondary">Max. Deposit</Typography>
                        <Typography variant="h6" fontWeight="medium">{product.maxDeposit}</Typography>
                    </Grid>
                    <Grid item xs>
                        <Typography variant="body2" color="text.secondary">Lock-in</Typography>
                        <Typography variant="h6" fontWeight="medium">{product.lockIn}</Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};


const FDProductsSection = ({ data }) => (
    <Section>
        <SectionTitle title="Invest in FDs With Trusted Partners" />
        <Box>
            {data.map(product => (
                <FDProductCard key={product.id} product={product} />
            ))}
        </Box>
    </Section>
);

// --- ENHANCED CALCULATOR SECTION ---
const FDCalculatorSection = () => {
    const [mode, setMode] = useState('maturity'); // 'maturity' or 'investment'
    const [amount, setAmount] = useState(100000);
    const [tenure, setTenure] = useState(12); // in months
    const [rate, setRate] = useState(8.4);
    const [targetAmount, setTargetAmount] = useState(1000000);

    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setMode(newMode);
        }
    };

    const { maturityAmount, interestEarned } = useMemo(() => {
        const p = amount;
        const r = rate / 100;
        const t = tenure / 12;
        const n = 4; // Compounded quarterly
        const maturity = p * Math.pow((1 + r / n), n * t);
        return { maturityAmount: maturity, interestEarned: maturity - p };
    }, [amount, tenure, rate]);

    const { requiredInvestment } = useMemo(() => {
        const A = targetAmount;
        const r = rate / 100;
        const t = tenure / 12;
        const n = 4; // Compounded quarterly
        const principal = A / Math.pow((1 + r / n), n * t);
        return { requiredInvestment: principal };
    }, [targetAmount, tenure, rate]);

    return (
        <Section sx={{ backgroundColor: 'background.paper' }}>
            <SectionTitle title="FD Returns Calculator" />
            <Grid container justifyContent="center">
                <Grid item xs={12} md={10} lg={8}>
                    <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
                        <ToggleButtonGroup
                            color="primary"
                            value={mode}
                            exclusive
                            onChange={handleModeChange}
                            aria-label="calculator mode"
                            fullWidth
                            sx={{ mb: 4 }}
                        >
                            <ToggleButton value="maturity">Calculate Maturity</ToggleButton>
                            <ToggleButton value="investment">Calculate Investment Needed</ToggleButton>
                        </ToggleButtonGroup>

                        {mode === 'maturity' ? (
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={7}>
                                    <CalculatorInput label="Investment Amount" value={amount} onChange={setAmount} unit="₹" min={5000} max={10000000} step={5000} />
                                    <CalculatorInput label="Investment Tenure" value={tenure} onChange={setTenure} unit="Months" min={6} max={120} step={1} />
                                    <CalculatorInput label="Interest Rate" value={rate} onChange={setRate} unit="% p.a." min={4} max={10} step={0.05} />
                                </Grid>
                                <Grid item xs={12} md={5} sx={{ textAlign: 'center', alignSelf: 'center' }}>
                                    <Typography color="text.secondary">Maturity Amount</Typography>
                                    <Typography variant="h4" color="primary" gutterBottom>₹{maturityAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                                    <Typography color="text.secondary">Assured Extra Earnings</Typography>
                                    <Typography variant="h6" gutterBottom>₹{interestEarned.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                                    <Button variant="contained" size="large" href={FD_CTA_LINK} target="_blank">Deposit Now</Button>
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={7}>
                                    <CalculatorInput label="I want to earn" value={targetAmount} onChange={setTargetAmount} unit="₹" min={10000} max={50000000} step={10000} />
                                    <CalculatorInput label="Investment Tenure" value={tenure} onChange={setTenure} unit="Months" min={6} max={120} step={1} />
                                    <CalculatorInput label="Interest Rate" value={rate} onChange={setRate} unit="% p.a." min={4} max={10} step={0.05} />
                                </Grid>
                                <Grid item xs={12} md={5} sx={{ textAlign: 'center', alignSelf: 'center' }}>
                                    <Typography color="text.secondary">You need to invest</Typography>
                                    <Typography variant="h4" color="primary" gutterBottom>₹{requiredInvestment.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                                    <Typography color="text.secondary">to reach your goal.</Typography>
                                    <Button variant="contained" size="large" href={FD_CTA_LINK} target="_blank" sx={{ mt: 2 }}>Start Investing</Button>
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Section>
    );
};


const BenefitsSection = ({ data }) => (
    <Section>
        <SectionTitle title="Benefits of Fixed Deposit" />
        <Grid container spacing={4}>
            {data.map(benefit => (
                <Grid item xs={12} sm={6} md={3} key={benefit.title}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60, mx: 'auto', mb: 2 }}>
                            {React.cloneElement(benefit.icon, { sx: { color: 'white', fontSize: 30 } })}
                        </Avatar>
                        <Typography variant="h6">{benefit.title}</Typography>
                        <Typography color="text.secondary">{benefit.description}</Typography>
                    </Box>
                </Grid>
            ))}
        </Grid>
    </Section>
);

// --- NEW ALTERNATING BENEFITS SECTION ---
const AlternatingBenefitsSection = ({ data }) => (
    <Section sx={{ backgroundColor: 'background.paper' }}>
        {data.map((benefit, index) => (
            <Grid container spacing={4} alignItems="center" key={benefit.title} sx={{ mb: 4 }} direction={index % 2 === 0 ? 'row' : 'row-reverse'}>
                <Grid item xs={12} md={6}>
                    <Box sx={{
                        width: '100%',
                        height: { xs: 250, md: 350 },
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: 3
                    }}>
                        <img
                            src={benefit.imageUrl}
                            alt={benefit.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="h4" component="h3" gutterBottom>{benefit.title}</Typography>
                    <Typography variant="body1" color="text.secondary">{benefit.description}</Typography>
                </Grid>
            </Grid>
        ))}
    </Section>
);


// --- MAIN PAGE COMPONENT ---
const FixedDepositPage = () => {
    const mode = useSelector((state) => state.theme.mode);
    const theme = getAppTheme(mode);
    const seoData = getSeoData("/fixed-deposit");

    const pageSections = [
        { id: 'hero', component: <HeroSection data={heroData} /> },
        { id: 'products', component: <FDProductsSection data={fdProductsData} /> },
        { id: 'calculator', component: <FDCalculatorSection /> },
        { id: 'benefits', component: <BenefitsSection data={benefitsData} /> },
        { id: 'alternating-benefits', component: <AlternatingBenefitsSection data={alternatingBenefitsData} /> },
        { id: 'faq', component: <Section><FAQ questions={faqData} /></Section> },
    ];

    return (
        <ThemeProvider theme={theme}>
            <SEO {...seoData} />
            <Box sx={{ backgroundColor: 'background.default' }}>
                {pageSections.map(section => (
                    <React.Fragment key={section.id}>
                        {section.component}
                    </React.Fragment>
                ))}
            </Box>
            {/* --- ADDED DISCLAIMER SECTION --- */}
            <Box component="footer" sx={{ py: 3, backgroundColor: 'background.default' }}>
                <Container maxWidth="lg">
                    <Typography variant="caption" color="text.secondary" component="p" textAlign="center">
                        Disclaimer: All Fixed Deposit information, including interest rates and terms, is sourced from Tarrakki and is subject to change. Please verify all details on the respective issuer's website before investing.
                    </Typography>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

// --- FAQ Component (used inside a Section) ---
const FAQ = ({ questions }) => (
    <Box>
        <SectionTitle title="Fixed Deposit FAQs" />
        {questions.map((item, index) => (
            <Accordion key={index} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography color="text.secondary">{item.a}</Typography>
                </AccordionDetails>
            </Accordion>
        ))}
    </Box>
);

export default FixedDepositPage;
